import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import swalAlert from '../helpers/swalAlert'
import useSort from './useSort'
import usePaginator from './usePaginator'
import useFilter from './useFilter'
import auxFun from '../helpers/auxFun'

const useHandlerBasicActions = ({
  isUseDispatch,
  sortFieldInitial,
  sortInit,
  rowsPerPageInitial,
  getAll,
  createAction,
  otherCreateAction,
  removeAction,
  removeAllAction,
  editAction,
  additionalDependency,
}) => {
  const dispatch = useDispatch()
  const { formatMessage: msg } = useIntl()
  const { sortField, sortOrder, onSort, sort } = useSort(
    sortFieldInitial,
    sortInit,
  )
  const { firstRow, skip, limit, onPageChange } =
    usePaginator(rowsPerPageInitial)
  const { filter, updateFilter } = useFilter()

  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getAllItems()
  }, [filter, limit, skip, sort, additionalDependency])

  const clear = () => {
    setLoading(true)
    setData(null)
    setLoading(false)
  }

  const getAllItems = async () => {
    setLoading(true)
    if (isUseDispatch) {
      dispatch(getAll({ filter, limit, skip, sort }))
      return
    }
    const response = await getAll({ filter, limit, skip, sort })
    if (response) setData(response)
    setLoading(false)
  }

  const createCommun = async (inputs, actionCreate) => {
    let response
    if (isUseDispatch) {
      response = (await dispatch(actionCreate(inputs))).payload
    } else {
      response = await actionCreate(inputs)
      updateStateOnCreate(response)
    }
    return response
  }

  const create = async (inputs) => createCommun(inputs, createAction)

  const otherCreate = async (inputs) => createCommun(inputs, otherCreateAction)

  const removeCommon = async (actionRemove, title) => {
    let response

    // console.log('removeCommon', actionRemove)

    const confirm = await swalAlert.warning(
      msg({ id: title }),
      msg({ id: 'button.cancel' }),
      msg({ id: 'button.delete' }),
    )
    if (confirm.isConfirmed) {
      if (isUseDispatch) {
        response = await dispatch(actionRemove)?.payload
        // console.log('response', response)
      } else {
        response = await actionRemove()
        getAllItems()
      }
    }
    return response
  }

  const remove = async (id) => {
    const resp = await removeCommon(() => removeAction(id), 'swal.title.delete')
    return resp
  }

  const removeAll = async () => {
    const resp = await removeCommon(
      () => removeAllAction(),
      'swal.title.deleteAll',
    )
    return resp
  }

  const edit = async ({ _id, body }) => {
    let response
    if (isUseDispatch) {
      response = await dispatch(editAction({ _id, body }))?.payload
    } else {
      response = await editAction({ _id, body })

      updateStateOnEdit(response)
    }
    return response
  }

  const updateStateOnCreate = (response) => {
    if (response) {
      setData(
        auxFun.isIterable(data.resp)
          ? {
              ...data,
              resp: [...data.resp, response?.resp],
              total: +data.total + 1,
            }
          : response,
      )
    }
  }

  const updateStateOnRemove = (response) => {
    if (response) {
      const newData = auxFun.isIterable(data?.resp)
        ? {
            ...data,
            resp: data?.resp.filter(
              (d) =>
                !(d._id === response?.resp?._id || d._id === response?.resp),
            ),
            total: +data.total - 1,
          }
        : response
      setData(newData)
    }
  }

  const updateStateOnEdit = (response) => {
    if (response)
      setData(
        auxFun.isIterable(data?.resp)
          ? {
              ...data,
              resp: data.resp.map((s) => {
                if (s._id === response?.resp?._id) return response?.resp
                return s
              }),
            }
          : response,
      )
  }

  return {
    loading,
    clear,
    create,
    otherCreate,
    remove,
    removeAll,
    edit,
    data,
    reload: getAllItems,
    pagerOptions: {
      firstRow,
      limit,
      rowsPerPageInitial,
      onPageChange,
    },
    searchOptions: {
      updateFilter,
      filter,
    },
    sortOptions: {
      sortField,
      sortOrder,
      onSort,
    },
  }
}

export default useHandlerBasicActions
